import React, { useState, useMemo } from 'react';
import teacherService from '../../services/api/teacher';
import { useQuery } from 'react-query';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import { 
  FileText, 
  Download, 
  Calendar, 
  TrendingUp, 
  Users, 
  BookOpen, 
  ShieldCheck, 
  ChevronLeft, 
  Sparkles, 
  BarChart3,
  ArrowRight,
  DollarSign,
  Layers,
  Star
} from 'lucide-react';

const TeacherReports = () => {
  const [selectedPeriod, setSelectedPeriod] = useState('month');
  const [selectedReport, setSelectedReport] = useState('overview');

  // جلب بيانات التقارير بناءً على الفلاتر (إعدادات محسّنة)
  const { data: reports, isLoading, error } = useQuery(
    ['teacher-reports', selectedPeriod, selectedReport],
    () => {
      const params = new URLSearchParams();
      params.append('period', selectedPeriod);
      params.append('type', selectedReport);
      
      // Only add year if it's a valid number and not the default year
      const currentYear = new Date().getFullYear();
      if (currentYear && currentYear > 2000) {
        params.append('year', currentYear.toString());
      }
      
      return teacherService.getTeacherReports(params.toString());
    },
    {
      keepPreviousData: true,
      staleTime: 60_000,
      refetchOnWindowFocus: false,
      retry: false,
    }
  );

  // Update stats with actual data when available
  const statsWithRealData = useMemo(() => [
    { 
      label: 'إجمالي الكورسات', 
      value: reports?.data?.total_courses || 0, 
      icon: BookOpen, 
      color: 'text-blue-600', 
      bg: 'bg-blue-50', 
      growth: reports?.data?.courses_growth || '+12%' 
    },
    { 
      label: 'إجمالي الطلاب', 
      value: reports?.data?.total_students || 0, 
      icon: Users, 
      color: 'text-emerald-600', 
      bg: 'bg-emerald-50', 
      growth: reports?.data?.students_growth || '+9%' 
    },
    { 
      label: 'معدل الإكمال', 
      value: `${reports?.data?.completion_rate || 0}%`, 
      icon: TrendingUp, 
      color: 'text-purple-600', 
      bg: 'bg-purple-50', 
      growth: reports?.data?.completion_growth || '+15%' 
    },
    { 
      label: 'متوسط التقييم', 
      value: reports?.data?.avg_rating || '5.0', 
      icon: Star, 
      color: 'text-amber-500', 
      bg: 'bg-amber-50', 
      growth: reports?.data?.rating_growth || '+5%' 
    }
  ], [reports?.data]);

  // Error handling
  if (error) {
    return (
      <div className="flex flex-col items-center justify-center py-24 min-h-[500px]">
        <div className="w-20 h-20 bg-red-50 rounded-2xl flex items-center justify-center mb-6">
          <FileText className="text-red-500" size={32} />
        </div>
        <h3 className="text-xl font-black text-gray-900 mb-2">فشل في تحميل التقارير</h3>
        <p className="text-gray-500 text-center max-w-sm">حدث خطأ أثناء جلب بيانات التقارير. يرجى المحاولة مرة أخرى.</p>
        <button 
          onClick={() => window.location.reload()} 
          className="mt-6 bg-primary-600 text-white px-6 py-3 rounded-2xl font-black hover:bg-primary-700 transition-all"
        >
          إعادة المحاولة
        </button>
      </div>
    );
  }

  if (isLoading && !reports) {
    return (
      <div className="flex flex-col items-center justify-center py-24 min-h-[500px]">
        <LoadingSpinner size="large" />
        <p className="mt-4 text-gray-500 font-bold animate-pulse uppercase tracking-widest text-xs">جاري تحليل البيانات واستخراج التقارير...</p>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto space-y-10 px-4 sm:px-6 py-6 min-h-screen bg-gray-50/30">
      
      {/* Header Section */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-6 animate-in fade-in duration-500">
        <div className="space-y-1">
          <h1 className="text-3xl lg:text-4xl font-black text-gray-900 tracking-tight flex items-center gap-3">
            <BarChart3 className="text-primary-600 bg-primary-50 p-1.5 rounded-xl" size={36} />
            مركز التقارير والتحليل
          </h1>
          <p className="text-gray-500 font-medium flex items-center gap-2 mr-12">
            <ShieldCheck className="text-emerald-500" size={18} />
            راقب أداء كورساتك وتفاعل طلابك من خلال إحصائيات دقيقة ومباشرة
          </p>
        </div>
      </div>

      {/* Advanced Filter Toolkit */}
      <div className="bg-white rounded-[2.5rem] shadow-sm border border-gray-100 p-8 flex flex-col lg:flex-row items-center gap-8 relative overflow-hidden">
        <div className="absolute top-0 left-0 w-2 h-full bg-primary-600 opacity-20"></div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 flex-1 w-full">
          <div className="space-y-2">
            <label className="flex items-center gap-2 text-[10px] font-black text-gray-400 uppercase tracking-widest mr-2">
                <Layers size={14} className="text-primary-600" /> تصنيف التقرير المطلوب
            </label>
            <select
              value={selectedReport}
              onChange={(e) => setSelectedReport(e.target.value)}
              className="w-full px-5 py-4 bg-gray-50 border border-gray-100 rounded-2xl focus:ring-4 focus:ring-primary-500/10 focus:bg-white transition-all font-bold text-gray-700 appearance-none cursor-pointer"
            >
              <option value="overview">نظرة عامة على الأداء</option>
              <option value="students">تحليل تقدم الطلاب</option>
              <option value="courses">إحصائيات الكورسات</option>
              <option value="revenue">تقارير الإيرادات</option>
            </select>
          </div>
          
          <div className="space-y-2">
            <label className="flex items-center gap-2 text-[10px] font-black text-gray-400 uppercase tracking-widest mr-2">
                <Calendar size={14} className="text-primary-600" /> النطاق الزمني للبيانات
            </label>
            <select
              value={selectedPeriod}
              onChange={(e) => setSelectedPeriod(e.target.value)}
              className="w-full px-5 py-4 bg-gray-50 border border-gray-100 rounded-2xl focus:ring-4 focus:ring-primary-500/10 focus:bg-white transition-all font-bold text-gray-700 appearance-none cursor-pointer"
            >
              <option value="week">الأسبوع الحالي</option>
              <option value="month">الشهر الحالي</option>
              <option value="quarter">الربع سنوي الحالي</option>
              <option value="year">السنة المالية الحالية</option>
            </select>
          </div>
        </div>

        <div className="flex flex-col gap-3 w-full lg:w-auto pt-4 lg:pt-0">
          <button className="bg-primary-600 text-white px-8 py-4 rounded-2xl font-black shadow-xl shadow-primary-100 hover:bg-primary-700 transition-all flex items-center justify-center gap-2 transform active:scale-95">
            <Download size={20} /> استخراج التقرير
          </button>
          <p className="text-[9px] text-center font-bold text-gray-400 uppercase tracking-tighter">صيغ التصدير: PDF, Excel, CSV</p>
        </div>
      </div>

      {/* Dynamic Report Content */}
      <div className="space-y-10 animate-in fade-in slide-in-from-bottom-4 duration-700">
        
        {/* Type 1: Overview Report Cards */}
        {selectedReport === 'overview' && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {statsWithRealData.map((stat, i) => (
              <div key={i} className="bg-white p-6 rounded-[2.2rem] border border-gray-100 shadow-sm hover:shadow-lg transition-all group relative overflow-hidden">
                <div className="flex items-center justify-between mb-6">
                  <div className={`p-4 ${stat.bg} ${stat.color} rounded-2xl group-hover:scale-110 transition-transform`}>
                    <stat.icon size={24} />
                  </div>
                  <div className="flex items-center gap-1 text-[10px] font-black px-2.5 py-1 rounded-full border bg-green-50 text-green-600 border-green-100">
                    <TrendingUp size={10} />
                    {stat.growth}
                  </div>
                </div>
                <div>
                  <p className="text-[10px] font-black text-gray-400 uppercase tracking-[0.2em] leading-none mb-2">{stat.label}</p>
                  <p className="text-3xl font-black text-gray-900 leading-none">{stat.value}</p>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Type 2: Students Detailed List */}
        {selectedReport === 'students' && (
          <div className="bg-white rounded-[2.5rem] shadow-sm border border-gray-100 overflow-hidden group hover:shadow-xl transition-all duration-500">
            <div className="p-8 border-b border-gray-50 flex items-center justify-between bg-gray-50/30">
              <h2 className="text-xl font-black text-gray-900 tracking-tighter uppercase flex items-center gap-2">
                  <Users className="text-primary-600" /> تحليل تقدم الطلاب الفردي
              </h2>
              <span className="text-[10px] font-black text-gray-400 bg-white px-4 py-1.5 rounded-full border border-gray-100 uppercase tracking-widest">تحديث مباشر</span>
            </div>
            <div className="p-4 sm:p-8 space-y-4">
              {reports?.data?.students?.length > 0 ? (
                reports.data.students.map((student, index) => (
                  <div key={index} className="flex flex-col sm:flex-row items-center justify-between p-6 bg-gray-50/50 rounded-3xl border border-gray-50 group/item hover:bg-white hover:shadow-lg hover:border-primary-100 transition-all duration-300 gap-6">
                    <div className="flex items-center gap-5 w-full sm:w-auto">
                      <div className="w-14 h-14 bg-gradient-to-br from-primary-500 to-primary-700 rounded-2xl flex items-center justify-center text-white font-black shadow-lg group-hover/item:scale-110 transition-transform">
                        {student.name?.charAt(0).toUpperCase()}
                      </div>
                      <div>
                        <p className="text-lg font-black text-gray-800 leading-tight">{student.name}</p>
                        <p className="text-xs font-bold text-gray-400 uppercase mt-1">{student.email}</p>
                      </div>
                    </div>
                    <div className="w-full sm:w-64 space-y-2">
                        <div className="flex items-center justify-between">
                            <span className="text-[10px] font-black text-gray-400 uppercase tracking-widest">نسبة الإنجاز</span>
                            <span className="text-xs font-black text-primary-600">{student.progress}%</span>
                        </div>
                        <div className="w-full bg-gray-200 rounded-full h-2 overflow-hidden shadow-inner">
                            <div 
                                className="bg-primary-600 h-full rounded-full transition-all duration-1000 shadow-[0_0_8px_rgba(99,102,241,0.5)]" 
                                style={{ width: `${student.progress}%` }}
                            ></div>
                        </div>
                    </div>
                    <button className="p-2 text-gray-300 hover:text-primary-600 transition-colors">
                        <ChevronLeft size={24} />
                    </button>
                  </div>
                ))
              ) : (
                  <div className="py-20 text-center text-gray-400 italic">لا توجد بيانات طلاب لعرضها في هذه الفترة.</div>
              )}
            </div>
          </div>
        )}

        {/* Type 3: Courses Analytics List */}
        {selectedReport === 'courses' && (
          <div className="bg-white rounded-[2.5rem] shadow-sm border border-gray-100 overflow-hidden group hover:shadow-xl transition-all duration-500">
            <div className="p-8 border-b border-gray-50 flex items-center justify-between bg-gray-50/30">
              <h2 className="text-xl font-black text-gray-900 tracking-tighter uppercase flex items-center gap-2">
                  <BookOpen className="text-primary-600" /> إحصائيات أداء الكورسات
              </h2>
              <span className="text-[10px] font-black text-gray-400 bg-white px-4 py-1.5 rounded-full border border-gray-100 uppercase tracking-widest">شامل التقارير</span>
            </div>
            <div className="p-4 sm:p-8 grid grid-cols-1 md:grid-cols-2 gap-6">
              {reports?.data?.courses?.length > 0 ? (
                reports.data.courses.map((course, index) => (
                  <div key={index} className="flex flex-col p-8 bg-gray-50/50 rounded-[2.5rem] border border-gray-50 hover:bg-white hover:shadow-xl transition-all duration-300">
                    <div className="flex items-center gap-4 mb-6">
                        <div className="w-12 h-12 bg-emerald-50 text-emerald-600 rounded-xl flex items-center justify-center shadow-inner">
                            <BookOpen size={24} />
                        </div>
                        <div className="flex-1 min-w-0">
                            <h4 className="text-lg font-black text-gray-900 truncate">{course.title}</h4>
                            <p className="text-[10px] font-black text-primary-500 uppercase tracking-widest">{course.subject_name}</p>
                        </div>
                    </div>
                    
                    <div className="grid grid-cols-2 gap-4 mt-auto">
                        <div className="bg-white p-4 rounded-2xl border border-gray-100 text-center">
                            <p className="text-[10px] font-black text-gray-400 uppercase mb-1">الطلاب</p>
                            <p className="text-xl font-black text-gray-800">{course.student_count}</p>
                        </div>
                        <div className="bg-white p-4 rounded-2xl border border-gray-100 text-center">
                            <p className="text-[10px] font-black text-gray-400 uppercase mb-1">التقييم</p>
                            <div className="flex items-center justify-center gap-1">
                                <Star size={14} className="text-amber-500 fill-amber-500" />
                                <p className="text-xl font-black text-gray-800">{course.rating}</p>
                            </div>
                        </div>
                    </div>
                  </div>
                ))
              ) : (
                <div className="col-span-full py-20 text-center text-gray-400 italic">لا توجد كورسات مسجلة لهذا المدرس.</div>
              )}
            </div>
          </div>
        )}

        {/* Type 4: Revenue Reports */}
        {selectedReport === 'revenue' && (
          <div className="space-y-8">
            {/* Revenue Summary Cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              <div className="bg-white p-6 rounded-[2.2rem] border border-gray-100 shadow-sm hover:shadow-lg transition-all group relative overflow-hidden">
                <div className="flex items-center justify-between mb-6">
                  <div className="p-4 bg-emerald-50 text-emerald-600 rounded-2xl group-hover:scale-110 transition-transform">
                    <TrendingUp size={24} />
                  </div>
                  <div className="flex items-center gap-1 text-[10px] font-black px-2.5 py-1 rounded-full border bg-green-50 text-green-600 border-green-100">
                    <TrendingUp size={10} />
                    +12%
                  </div>
                </div>
                <div>
                  <p className="text-[10px] font-black text-gray-400 uppercase tracking-[0.2em] leading-none mb-2">إجمالي الإيرادات</p>
                  <p className="text-3xl font-black text-gray-900 leading-none">{reports?.data?.total_revenue || 0} ج.م</p>
                </div>
              </div>

              <div className="bg-white p-6 rounded-[2.2rem] border border-gray-100 shadow-sm hover:shadow-lg transition-all group relative overflow-hidden">
                <div className="flex items-center justify-between mb-6">
                  <div className="p-4 bg-blue-50 text-blue-600 rounded-2xl group-hover:scale-110 transition-transform">
                    <Users size={24} />
                  </div>
                  <div className="flex items-center gap-1 text-[10px] font-black px-2.5 py-1 rounded-full border bg-green-50 text-green-600 border-green-100">
                    <TrendingUp size={10} />
                    +8%
                  </div>
                </div>
                <div>
                  <p className="text-[10px] font-black text-gray-400 uppercase tracking-[0.2em] leading-none mb-2">إجمالي الاشتراكات</p>
                  <p className="text-3xl font-black text-gray-900 leading-none">{reports?.data?.total_enrollments || 0}</p>
                </div>
              </div>

              <div className="bg-white p-6 rounded-[2.2rem] border border-gray-100 shadow-sm hover:shadow-lg transition-all group relative overflow-hidden">
                <div className="flex items-center justify-between mb-6">
                  <div className="p-4 bg-purple-50 text-purple-600 rounded-2xl group-hover:scale-110 transition-transform">
                    <Star size={24} />
                  </div>
                  <div className="flex items-center gap-1 text-[10px] font-black px-2.5 py-1 rounded-full border bg-green-50 text-green-600 border-green-100">
                    <TrendingUp size={10} />
                    +5%
                  </div>
                </div>
                <div>
                  <p className="text-[10px] font-black text-gray-400 uppercase tracking-[0.2em] leading-none mb-2">الطلاب الفريدين</p>
                  <p className="text-3xl font-black text-gray-900 leading-none">{reports?.data?.unique_students || 0}</p>
                </div>
              </div>

              <div className="bg-white p-6 rounded-[2.2rem] border border-gray-100 shadow-sm hover:shadow-lg transition-all group relative overflow-hidden">
                <div className="flex items-center justify-between mb-6">
                  <div className="p-4 bg-amber-50 text-amber-500 rounded-2xl group-hover:scale-110 transition-transform">
                    <DollarSign size={24} />
                  </div>
                  <div className="flex items-center gap-1 text-[10px] font-black px-2.5 py-1 rounded-full border bg-green-50 text-green-600 border-green-100">
                    <TrendingUp size={10} />
                    +3%
                  </div>
                </div>
                <div>
                  <p className="text-[10px] font-black text-gray-400 uppercase tracking-[0.2em] leading-none mb-2">متوسط السعر</p>
                  <p className="text-3xl font-black text-gray-900 leading-none">{reports?.data?.avg_price || 0} ج.م</p>
                </div>
              </div>
            </div>

            {/* Monthly Revenue Breakdown */}
            <div className="bg-white rounded-[2.5rem] shadow-sm border border-gray-100 overflow-hidden group hover:shadow-xl transition-all duration-500">
              <div className="p-8 border-b border-gray-50 flex items-center justify-between bg-gray-50/30">
                <h2 className="text-xl font-black text-gray-900 tracking-tighter uppercase flex items-center gap-2">
                  <BarChart3 className="text-primary-600" /> تفصيل الإيرادات الشهرية
                </h2>
                <span className="text-[10px] font-black text-gray-400 bg-white px-4 py-1.5 rounded-full border border-gray-100 uppercase tracking-widest">12 شهر</span>
              </div>
              <div className="p-4 sm:p-8">
                {reports?.data?.monthly_breakdown?.length > 0 ? (
                  <div className="space-y-4">
                    {reports.data.monthly_breakdown.map((month, index) => (
                      <div key={index} className="flex items-center justify-between p-6 bg-gray-50/50 rounded-3xl border border-gray-50 hover:bg-white hover:shadow-lg transition-all duration-300">
                        <div className="flex items-center gap-4">
                          <div className="w-12 h-12 bg-primary-100 text-primary-600 rounded-xl flex items-center justify-center font-black">
                            {month.month}
                          </div>
                          <div>
                            <p className="font-black text-gray-800">الشهر {month.month}</p>
                            <p className="text-[10px] text-gray-400">{month.year}</p>
                          </div>
                        </div>
                        <div className="flex items-center gap-8">
                          <div className="text-center">
                            <p className="text-[10px] font-black text-gray-400 uppercase mb-1">اشتراكات</p>
                            <p className="text-xl font-black text-gray-800">{month.enrollments}</p>
                          </div>
                          <div className="text-center">
                            <p className="text-[10px] font-black text-gray-400 uppercase mb-1">إيرادات</p>
                            <p className="text-xl font-black text-emerald-600">{month.revenue} ج.م</p>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="py-20 text-center text-gray-400 italic">لا توجد بيانات إيرادات لعرضها في هذه الفترة.</div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Footer Support Card */}
      <div className="bg-gray-900 rounded-[3rem] p-10 text-white flex flex-col md:flex-row items-center justify-between gap-8 relative overflow-hidden group shadow-2xl">
        <div className="absolute top-0 right-0 w-64 h-64 bg-primary-600/20 rounded-full blur-3xl -z-0"></div>
        <div className="relative z-10 text-center md:text-right space-y-2">
            <h3 className="text-2xl font-black flex items-center gap-2 justify-center md:justify-start leading-tight">
                <Sparkles className="text-primary-500" /> هل تحتاج إلى تحليلات مخصصة؟
            </h3>
            <p className="text-gray-400 font-medium max-w-xl leading-relaxed">فريق تحليل البيانات متاح لمساعدتك في الحصول على تقارير تفصيلية إضافية حول سلوك الطلاب أو توقعات الإيرادات.</p>
        </div>
        <button 
            onClick={() => window.location.href = 'mailto:data@almanassa.com'}
            className="relative z-10 bg-white text-gray-900 px-10 py-4 rounded-2xl font-black flex items-center gap-2 shadow-xl hover:bg-primary-50 transition-all transform active:scale-95"
        >
            تواصل مع المحللين <ArrowRight size={20} className="rotate-180" />
        </button>
      </div>
    </div>
  );
};

export default TeacherReports;